import { useEffect } from "react";

/**
 * Makes every `.data-table` / `.table` render as stacked "label: value" cards on
 * phones (see the `@media (max-width: 720px)` block in theme.css) without touching
 * each page: it copies each column's `<th>` text onto the matching `<td>` as a
 * `data-label`, which the CSS surfaces via `td::before`. Re-applies on route change
 * and on any DOM mutation under the main content (paging, filtering, async loads).
 */
export function useResponsiveTableLabels(pathname: string): void {
  useEffect(() => {
    const root = document.querySelector("main");
    if (!root) return;

    const applyAll = () => {
      root.querySelectorAll<HTMLTableElement>("table.data-table, table.table").forEach((t) => {
        const headers = Array.from(t.tHead?.rows?.[0]?.cells ?? []).map(
          (c) => c.textContent?.trim() ?? ""
        );
        if (!headers.length) return;
        for (const body of Array.from(t.tBodies)) {
          for (const row of Array.from(body.rows)) {
            Array.from(row.cells).forEach((cell, i) => {
              const label = headers[i];
              if (label && cell.getAttribute("data-label") !== label) {
                cell.setAttribute("data-label", label);
              }
            });
          }
        }
      });
    };

    let raf = 0;
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(applyAll);
    };

    schedule();
    // childList only → our own setAttribute calls don't re-trigger the observer.
    const obs = new MutationObserver(schedule);
    obs.observe(root, { childList: true, subtree: true });
    return () => {
      cancelAnimationFrame(raf);
      obs.disconnect();
    };
  }, [pathname]);
}
